import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { matchesSideNorm } from "./orderFilterHelpers";
import AuthService from "../../../api/services/AuthService";
import {
  decNum,
  decStr,
  fmtTs,
  formatOptionsTradeId,
  resolveOptionsTradeUserView,
} from "../options/optionsDataHelpers";

const ORDER_DESKTOP_COL_COUNT = 8;

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btcusdt", label: "BTCUSDT" },
  { value: "ethusdt", label: "ETHUSDT" },
  { value: "bnbusdt", label: "BNBUSDT" },
];

const SIDE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
];

function extractTrades(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d?.trades)) return d.trades;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d)) return d;
  if (Array.isArray(res.trades)) return res.trades;
  if (Array.isArray(res.items)) return res.items;
  return [];
}

function matchesOptionsContractFilter(symbol, filter) {
  if (!filter || filter === "all") return true;
  const sym = String(symbol || "").toUpperCase();
  const fc = String(filter).replace(/[/_]/g, "").toUpperCase();
  if (!sym || !fc) return false;
  if (sym === fc) return true;
  const base = fc.replace(/USDT$|USDC$|BUSD$/, "");
  return Boolean(base) && (sym.startsWith(`${base}-`) || sym.startsWith(fc));
}

function formatQty(v) {
  const n = decNum(v);
  if (!(n > 0) && n !== 0) return "—";
  return decStr(v) || String(n);
}

function mapTradeRow(item, idx, userId) {
  const view = resolveOptionsTradeUserView(item, userId);
  const sideRaw = String(item?.side || "").toUpperCase();
  const side = view.side || (sideRaw === "BUY" ? "Buy" : sideRaw === "SELL" ? "Sell" : sideRaw || "—");
  const feeNum = view.fee > 0 ? view.fee : decNum(item?.fee ?? item?.taker_fee ?? item?.maker_fee);
  const feeAsset = view.feeAsset || item?.fee_asset || "USDT";
  const id = item?.trade_id || item?._id || item?.id || `opt-trade-${idx}`;
  return {
    id,
    contract: item?.symbol || item?.contract || item?.instrument || "—",
    side,
    size: formatQty(item?.quantity),
    fillPrice: decNum(item?.price) > 0 ? decStr(item.price) : "—",
    fee: feeNum > 0 ? `${feeNum} ${feeAsset}` : "—",
    orderId: item?.order_id || item?.client_order_id || "—",
    tradeId: formatOptionsTradeId(item?.trade_id || item?._id),
    tradedAt: fmtTs(item?.created_at || item?.traded_at || item?.timestamp),
  };
}

const OptionsTradeHistory = () => {
  const [contract, setContract] = useState("all");
  const [side, setSide] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [rows, setRows] = useState([]);

  const loadTrades = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setRows([]);
      return;
    }
    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    try {
      const res = await AuthService.optionsLiveTrades({ limit: 100 });
      setRows(extractTrades(res).map((item, idx) => mapTradeRow(item, idx, userId)));
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void loadTrades();
  }, [loadTrades]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const filteredRows = useMemo(() => {
    return rows.filter(
      (r) => matchesOptionsContractFilter(r.contract, contract) && matchesSideNorm(r.side, side)
    );
  }, [rows, contract, side]);

  const resetFilters = () => {
    setContract("all");
    setSide("all");
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter options trade history">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}oth-dd-contract`}
          label="Contract"
          value={contract}
          options={CONTRACT_OPTIONS}
          onChange={setContract}
          menuKey={`${pref}oth-contract`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}oth-dd-side`}
          label="Side"
          value={side}
          options={SIDE_OPTIONS}
          onChange={setSide}
          menuKey={`${pref}oth-side`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={8} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap so-optionsTradeHistoryPage">
          <main className="so-page" aria-labelledby="so-options-trade-history-title">
            <h1 id="so-options-trade-history-title" className="so-page__title">
            Trade History
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
                { label: "Order History", to: "/user_profile/orders/options_order_history" },
                { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
                { label: "Position History", to: "/user_profile/orders/options_position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking so-optionsTradeHistoryTableShell">
              <table className="so-table so-table--rowLines so-table--air so-table--optionsTradeHistory">
                <colgroup>
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--contract" />
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--side" />
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--size" />
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--fill" />
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--fee" />
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--orderId" />
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--tradeId" />
                  <col className="so-optionsTradeHistory__cw so-optionsTradeHistory__cw--tradedAt" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Side</th>
                    <th scope="col">Size</th>
                    <th scope="col">Fill Price</th>
                    <th scope="col">Fee</th>
                    <th scope="col">Order ID</th>
                    <th scope="col">Trade ID</th>
                    <th scope="col">Traded at</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : filteredRows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <strong className="so-optionsTradeHistory__strong">{r.contract}</strong>
                          </td>
                          <td>
                            <span className={r.side === "Buy" ? "so-side--buy so-optionsTradeHistory__pill" : "so-side--sell so-optionsTradeHistory__pill"}>
                              {r.side}
                            </span>
                          </td>
                          <td className="so-num">{r.size}</td>
                          <td className="so-num">{r.fillPrice}</td>
                          <td className="so-num">{r.fee}</td>
                          <td className="so-num so-optionsTradeHistory__cellClip">{r.orderId}</td>
                          <td className="so-num so-optionsTradeHistory__cellClip">{r.tradeId}</td>
                          <td className="so-num so-optionsTradeHistory__cellTime">{r.tradedAt}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Options Trade History</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
              { label: "Order History", to: "/user_profile/orders/options_order_history" },
              { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
              { label: "Position History", to: "/user_profile/orders/options_position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-options-trade-history-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
            />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            filteredRows.map((r) => (
              <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Options trade details">
                <div className="so-mobileCard__row">
                  <span>Contract</span>
                  <strong className="so-optionsTradeHistory__strong">{r.contract}</strong>
                </div>
                <div className="so-mobileCard__row">
                  <span>Side</span>
                  <span className={r.side === "Buy" ? "so-side--buy" : "so-side--sell"}>{r.side}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Size</span>
                  <span className="so-num">{r.size}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fill Price</span>
                  <span className="so-num">{r.fillPrice}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fee</span>
                  <span className="so-num">{r.fee}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Order ID</span>
                  <span className="so-num">{r.orderId}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Trade ID</span>
                  <span className="so-num">{r.tradeId}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Traded at</span>
                  <span className="so-num">{r.tradedAt}</span>
                </div>
              </OrderMobileCardShell>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OptionsTradeHistory;
